import { FieldTypes } from '../../../constants'
import {
  DateField,
  DropdownField,
  DropdownOptionItem,
  Field,
  FieldMap,
  ListField,
  MultipleSelectField,
  TextField,
} from '../../../types/forms/fields'
import {
  childsGestationAndBirthField,
  pregnancyOutcomeField,
  pregnancyOutcomeOtherField,
  previousPregnanciesField,
} from '../../pregnancy'
import { getBooleanHistoryField } from '../../shared'
import { getConditionOrDiseaseFields, getDetailsField } from '../shared'

export const ovarianCystsFields = getConditionOrDiseaseFields(
  'ovarianCysts',
  'Ovarian cysts, PCOS, or undiagnosed abnormal periods',
)

const nonSexuallyTransmittedVaginalInfectionsOptions: Array<DropdownOptionItem> = [
  {
    text: 'Bacterial vaginosis (BV)',
    id: 'bacterial-vaginosis',
  },
  {
    text: 'Candidiasis (yeast)',
    id: 'candidiasis',
  },
  {
    text: 'Pelvic inflammatory disease (PID)',
    id: 'pelvic-inflammatory-disease',
  },
  {
    text: 'Trichomoniasis',
    id: 'trichomoniasis',
  },
  {
    text: 'Other',
    id: 'other',
  },
]

export const nonSexuallyTransmittedVaginalInfectionsFields = getConditionOrDiseaseFields(
  'nonSexuallyTransmittedVaginalInfections',
  'Non-sexually transmitted vaginal infection',
  nonSexuallyTransmittedVaginalInfectionsOptions,
)

const sexuallyTransmittedInfectionsOptions: Array<DropdownOptionItem> = [
  {
    text: 'Yeast infection',
    id: 'yeast-infection',
  },
  {
    text: 'Bacterial vaginosis (BV)',
    id: 'bacterial-vaginosis',
  },
  {
    text: 'Chlamydia',
    id: 'chlamydia',
  },
  {
    text: 'Gonorrhea',
    id: 'gonorrhea',
  },
  {
    text: 'Hepatitis',
    id: 'hepatitis',
  },
  {
    text: 'Herpes, genital',
    id: 'herpes-genital',
  },
  {
    text: 'Human papillomavirus (HPV)',
    id: 'hpv',
  },
  {
    text: 'Pelvic inflammatory disease (PID)',
    id: 'pelvic-inflammatory-disease',
  },
  {
    text: 'Public lice (crabs)',
    id: 'public-lice',
  },
  {
    text: 'Syphilis',
    id: 'syphilis',
  },
  {
    text: 'Trichomoniasis',
    id: 'trichomoniasis',
  },
  {
    text: 'HIV',
    id: 'hiv',
  },
  {
    text: 'Other',
    id: 'other',
  },
]

export const sexuallyTransmittedInfectionsFields = getConditionOrDiseaseFields(
  'sexuallyTransmittedInfections',
  'Sexually transmitted infection',
  sexuallyTransmittedInfectionsOptions,
)

export const endometriosisFields = getConditionOrDiseaseFields(
  'endometriosis',
  'Endometriosis, difficult periods, or unexplained abdominal, pelvic or vulvar pain',
)

export const motherTookDESWhilePregnantField: DropdownField = {
  placeholder: 'Did your mother take DES while pregnant?',
  type: FieldTypes.DROPDOWN,
  inputType: 'radio',
  condition: v => !!v?.uterineOrCervicalAbnormalities,
  options: [
    { id: 'yes', text: 'Yes' },
    { id: 'no', text: 'No' },
    { id: 'unknown', text: 'Unknown' },
  ],
}

export const bornWithNonTypicalGenitaliaField: DropdownField = {
  placeholder: 'Were you born with non-typical genitalia?',
  type: FieldTypes.DROPDOWN,
  inputType: 'radio',
  condition: v => !!v?.uterineOrCervicalAbnormalities,
  options: [
    { id: 'yes', text: 'Yes' },
    { id: 'no', text: 'No' },
    { id: 'other', text: 'Other' },
  ],
}

export const bornWithNonTypicalGenitaliaOtherField: TextField = {
  placeholder: 'Please specify',
  type: FieldTypes.TEXT,
  condition: v => !!v?.uterineOrCervicalAbnormalities && v?.bornWithNonTypicalGenitalia === 'other',
}

export const uterineOrCervicalAbnormalitiesFields = getConditionOrDiseaseFields(
  'uterineOrCervicalAbnormalities',
  'Uterine or cervical aormalities',
  undefined,
  undefined,
  {
    motherTookDESWhilePregnant: motherTookDESWhilePregnantField,
    bornWithNonTypicalGenitalia: bornWithNonTypicalGenitaliaField,
    bornWithNonTypicalGenitaliaOther: bornWithNonTypicalGenitaliaOtherField,
  },
)

export const uterineProlapseFields = getConditionOrDiseaseFields(
  'uterineProlapse',
  'Uterine prolapse, rectocele, or cystocele',
)

const nonCancerousBreastConditionsOptions: Array<DropdownOptionItem> = [
  {
    text: 'Insufficient glandular tissue',
    id: 'insufficient-glandular-tissue',
  },
  {
    text: 'Mastitis',
    id: 'mastitis',
  },
  {
    text: 'Benign tumors',
    id: 'benign-tumors',
  },
  {
    text: 'Other',
    id: 'other',
  },
]

export const nonCancerousBreastConditionsFields = getConditionOrDiseaseFields(
  'nonCancerousBreastConditions',
  'Non-cancerous breast conditions',
  nonCancerousBreastConditionsOptions,
)

export const reasonPeriodStoppedField: MultipleSelectField = {
  type: FieldTypes.MULTIPLE_SELECT,
  placeholder: 'Reason period stopped',
  options: [
    {
      text: 'Historectomy/Oophorectomy',
      id: 'historectomy-oophorectomy',
    },
    {
      text: 'Radiation therapy or chemotherapy',
      id: 'radiation-therapy-or-chemotherapy',
    },
    {
      text: 'Hormonal medication',
      id: 'hormonal-medication',
    },
    {
      text: 'Autoimmune disorder (including thyroid, lupus, rheumatoid arthritis, etc.)',
      id: 'autoimmune-disorder',
    },
    {
      text: "Turner's syndrome or other chromosomal diagnosis",
      id: 'turners-or-other-chromosomal',
    },
    {
      text: 'Genetic',
      id: 'genetic',
    },
    {
      text: 'Unknown',
      id: 'unknown',
    },
    {
      text: 'Other',
      id: 'other',
    },
  ],
  condition: v => !!v?.earlyMenopause,
}

export const earlyMenopauseFields = getConditionOrDiseaseFields(
  'earlyMenopause',
  'Early menopause (before age 40)',
  undefined,
  undefined,
  {
    reasonPeriodStopped: reasonPeriodStoppedField,
  },
)

// export const hasHadMammogramFields = getConditionOrDiseaseFields(
//   'hasHadMammogram',
//   'Has had mammogram',
//   undefined,
//   'Details (include dates, results, etc.)',
// )
export const hasHadMammogramFields: Record<string, Field> = {
  hasHadMammogram: getBooleanHistoryField('Has had mammogram'),
  mostRecentMammogramDate: {
    type: FieldTypes.DATE,
    placeholder: 'Date of most recent mammogram',
    condition: v => !!v?.hasHadMammogram,
  },
  mammogramDetails: {
    type: FieldTypes.TEXTAREA,
    placeholder: 'Details (include results, concerns, etc.)',
    condition: v => !!v?.hasHadMammogram,
  },
}

const breastSurgeryOrProceduresOptions: Array<DropdownOptionItem> = [
  {
    text: 'Breast reduction',
    id: 'breast-reduction',
  },
  {
    text: 'Abcess',
    id: 'abcess',
  },
  {
    text: 'Breast augmentation',
    id: 'breast-augmentation',
  },
  {
    text: 'Lumpectomy',
    id: 'lumpectomy',
  },
  {
    text: 'Other type of breast procedure',
    id: 'other',
  },
]

export const breastSurgeryOrProceduresFields = getConditionOrDiseaseFields(
  'breastSurgeryOrProcedures',
  'Breast surgery or procedures',
  breastSurgeryOrProceduresOptions,
)

const mostRecentPapSmearField: DateField = {
  type: FieldTypes.DATE,
  placeholder: 'Date of most recent pap smear',
  condition: v => !!v?.hasHadPapSmear,
}

export const hasHadPapSmearFields = getConditionOrDiseaseFields(
  'hasHadPapSmear',
  'Has had pap smear',
  undefined,
  'Details (include dates, results, etc.)',
  {
    mostRecentPapSmear: mostRecentPapSmearField,
  },
)

export const gynHistoryWithoutPregnanciesField: FieldMap = {
  name: 'GYN History',
  children: {
    ...ovarianCystsFields,
    ...nonSexuallyTransmittedVaginalInfectionsFields,
    ...sexuallyTransmittedInfectionsFields,
    ...endometriosisFields,
    ...uterineOrCervicalAbnormalitiesFields,
    ...uterineProlapseFields,
    ...nonCancerousBreastConditionsFields,
    ...earlyMenopauseFields,
    ...hasHadMammogramFields,
    ...breastSurgeryOrProceduresFields,
    ...hasHadPapSmearFields,
  },
}

export const additionalPregnanciesField: ListField = {
  ...previousPregnanciesField,
  optional: true,
  itemName: 'additional pregnancy',
  name: 'Additional Pregnancies',
}

export const existingPregnancyField: FieldMap = {
  name: 'Existing Pregnancy',
  children: {
    hasInnacuracies: getBooleanHistoryField('Does any of the above information look incorrect?'),
    innacuraciesDetails: getDetailsField(
      'hasInnacuracies',
      'Please provide any details about the incorrect information',
    ),
    pregnancyOutcome: pregnancyOutcomeField,
    pregnancyOutcomeOther: pregnancyOutcomeOtherField,
    childsGestationAndBirth: childsGestationAndBirthField,
  },
}

export const gynHistoryField: FieldMap = {
  name: 'GYN History',
  children: {
    additionalPregnancies: additionalPregnanciesField,
    ...gynHistoryWithoutPregnanciesField.children,
  },
}
